import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type Column,
  type ColumnDef,
  type PaginationState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { m } from 'framer-motion';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  SlidersHorizontal,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from './button';

/**
 * Tabla de datos genérica (rebrand M8): un componente para todas las tablas
 * del panel, sobre `@tanstack/react-table` — headless, así que el ordenado,
 * el filtro y la paginación corren sin recalcular ni re-renderizar más de lo
 * que hace falta (memoizado por la propia librería), y el marcado sigue
 * siendo un `<table>` normal por debajo.
 *
 * Vive fuera de `index.tsx` a propósito, y es `index.tsx` quien la reexporta
 * (`export * from './data-table'`): este archivo no importa nada de
 * `index.tsx` — solo `Button`, directo de `./button` — para no crear un ciclo
 * entre los dos.
 *
 * Sin animar filas por omisión: una tabla que se reordena u ordena a cada
 * tecla no puede pagar una transición por fila sin sentirse más lenta que
 * antes — mismo motivo por el que `Toasts` no anima su reflow con `layout`
 * en vez de con opacidad/transform. `animateRows` existe para la excepción:
 * una lista que se recarga entera de vez en cuando (un listado de
 * directorio, no una tabla que se resuelve en cada tecla) sí se beneficia de
 * la entrada escalonada, y ahí el costo no se paga en cada interacción.
 */

/**
 * `header` casi siempre es una función (`DataTableColumnHeader`), no un
 * string — así que el menú de columnas no puede leer el título ahí. `label`
 * es la única fuente para ese texto; `title` en `DataTableColumnHeader` es
 * un valor aparte porque vive dentro de la función `header`, no en `meta`.
 */
declare module '@tanstack/react-table' {
  // Los dos type params son parte de la firma que se está ampliando —
  // `ColumnMeta` los declara así— y hay que repetirlos aunque `label` no los
  // use.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    label?: string;
  }
}

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(' ');

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
}: {
  column: Column<TData, TValue>;
  title: string;
  className?: string;
}) {
  if (!column.getCanSort()) {
    return <div className={cx('font-medium', className)}>{title}</div>;
  }

  const sorted = column.getIsSorted();
  const Icon = sorted === 'asc' ? ArrowUp : sorted === 'desc' ? ArrowDown : ArrowUpDown;

  return (
    <button
      type="button"
      onClick={column.getToggleSortingHandler()}
      className={cx(
        '-ml-2 inline-flex items-center gap-1 rounded px-2 py-1 font-medium',
        'hover:bg-accent/60',
        className,
      )}
    >
      {title}
      <Icon className={cx('size-3.5', sorted ? 'opacity-100' : 'opacity-40')} />
    </button>
  );
}

/**
 * Selector de columnas visibles. Sin Radix: un panel posicionado a mano y un
 * `mousedown` en `document` para cerrarlo, igual que `LeafActionsPopover` —
 * una dependencia menos por una interacción que no la necesita.
 */
function ColumnVisibilityMenu<TData>({ columns }: { columns: Column<TData, unknown>[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const hideable = columns.filter((column) => column.getCanHide());
  if (hideable.length === 0) return null;

  return (
    <div ref={ref} className="relative">
      <Button variant="ghost" size="sm" onClick={() => setOpen((value) => !value)}>
        <SlidersHorizontal className="size-3.5" />
        Columnas
      </Button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 grid w-48 gap-0.5 rounded-lg border border-black/10 bg-[var(--occ-surface)] p-1.5 text-sm shadow-lg dark:border-white/10">
          {hideable.map((column) => (
            <label
              key={column.id}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-accent/50"
            >
              <input
                type="checkbox"
                checked={column.getIsVisible()}
                onChange={column.getToggleVisibilityHandler()}
                className="size-3.5 accent-[var(--occ-brand)]"
              />
              <span className="truncate">
                {column.columnDef.meta?.label ??
                  (typeof column.columnDef.header === 'string'
                    ? column.columnDef.header
                    : column.id)}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export type DataTableProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  /** Fila a fila, para navegar al hacer clic. Sin esto, la fila no reacciona al clic. */
  onRowClick?: (row: TData) => void;
  /** Texto cuando `data` está vacío. */
  emptyMessage?: string;
  /** Filas por página. `0` desactiva la paginación (todo en una sola página). */
  pageSize?: number;
  /** Muestra el selector de columnas visibles. Por omisión, sí. */
  enableColumnVisibility?: boolean;
  /**
   * Entrada escalonada por fila (fade + slide), capada a las primeras 12 para
   * que una página llena no tarde más en sentirse lista cuantas más filas
   * tenga. Pensada para listas que se recargan enteras de vez en cuando, no
   * para una tabla que se reordena en cada tecla — ver el comentario de arriba.
   */
  animateRows?: boolean;
  className?: string;
};

const DEFAULT_PAGE_SIZE = 25;

export function DataTable<TData, TValue>({
  columns,
  data,
  onRowClick,
  emptyMessage = 'Sin resultados.',
  pageSize = DEFAULT_PAGE_SIZE,
  enableColumnVisibility = true,
  animateRows = false,
  className,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  // Controlado y no dejado a `initialState`: sin esto, cada refresco de datos
  // en vivo (el WebSocket de salud de nodos, por ejemplo) le hacía creer a
  // react-table que había una tabla nueva y `autoResetPageIndex` la mandaba
  // de vuelta a la página 1 a los pocos segundos — el operador perdía el
  // lugar en cuanto llegaba el siguiente latido. Con el estado aquí arriba,
  // la página sobrevive a que `data` cambie de referencia.
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize });

  const paginated = pageSize > 0;

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnVisibility, ...(paginated ? { pagination } : {}) },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(paginated
      ? {
          getPaginationRowModel: getPaginationRowModel(),
          onPaginationChange: setPagination,
          // Es justo el reflejo del comentario de arriba: sin esto,
          // react-table reinstaura el reseteo automático que el estado
          // controlado por sí solo no evita.
          autoResetPageIndex: false,
        }
      : {}),
  });

  const rows = table.getRowModel().rows;
  const pageCount = table.getPageCount();

  // El reemplazo manual de lo que `autoResetPageIndex` hacía antes, pero solo
  // para el caso legítimo: si un filtro de verdad reduce los datos y la
  // página en la que estaba el operador ya no existe, se cae a la última
  // válida en vez de enseñar una tabla vacía. Un refresco en vivo que no
  // cambia cuántas páginas hay nunca dispara esto.
  useEffect(() => {
    if (!paginated) return;
    if (pageCount > 0 && pagination.pageIndex > pageCount - 1) {
      setPagination((current) => ({ ...current, pageIndex: pageCount - 1 }));
    }
  }, [paginated, pageCount, pagination.pageIndex]);

  return (
    <div className={cx('grid gap-3', className)}>
      {enableColumnVisibility && (
        <div className="flex justify-end">
          <ColumnVisibilityMenu columns={table.getAllLeafColumns()} />
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wide opacity-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="pb-2 pr-3 font-medium">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-6 text-center text-sm opacity-60">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row, index) => {
                const cells = row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="py-2 pr-3">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ));
                const rowClassName = cx(
                  'border-t border-black/5 dark:border-white/10',
                  onRowClick && 'cursor-pointer hover:bg-accent/30',
                );
                const onClick = onRowClick ? () => onRowClick(row.original) : undefined;

                return animateRows ? (
                  <m.tr
                    key={row.id}
                    onClick={onClick}
                    className={rowClassName}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.15, delay: Math.min(index, 12) * 0.02 }}
                  >
                    {cells}
                  </m.tr>
                ) : (
                  <tr key={row.id} onClick={onClick} className={rowClassName}>
                    {cells}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {paginated && pageCount > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs opacity-70">
          <span>
            {rows.length === 0
              ? '0 filas'
              : `Página ${pagination.pageIndex + 1} de ${pageCount} · ${data.length} filas`}
          </span>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Primera página"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.setPageIndex(0)}
            >
              <ChevronsLeft className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Página anterior"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.previousPage()}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Página siguiente"
              disabled={!table.getCanNextPage()}
              onClick={() => table.nextPage()}
            >
              <ChevronRight className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Última página"
              disabled={!table.getCanNextPage()}
              onClick={() => table.setPageIndex(pageCount - 1)}
            >
              <ChevronsRight className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export type { ColumnDef } from '@tanstack/react-table';
